import React, { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import API from "../services/api";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  ArrowRight, CheckCircle, AlertCircle, FileText,
  Clock, Wallet, CalendarX, User, UserPlus, ShieldCheck, Users2, GraduationCap,
} from "lucide-react";
import { FORM_GRADE_OPTIONS, getOptionLabel } from "../utils/academicLevels";

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency", currency: "KES", maximumFractionDigits: 0,
  }).format(amt || 0);

const STATUS_COLORS = {
  DRAFT: "bg-gray-100 text-gray-600",
  SUBMITTED: "bg-blue-100 text-blue-700",
  UNDER_REVIEW: "bg-yellow-100 text-yellow-700",
  APPROVED: "bg-green-100 text-green-700",
  REJECTED: "bg-red-100 text-red-700",
};

const StudentDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profileStatus, setProfileStatus] = useState("loading");
  const [profile, setProfile] = useState(null);
  const [application, setApplication] = useState(null);
  const [disbursement, setDisbursement] = useState(null);
  const [activeCycle, setActiveCycle] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const res = await API.get("/student/profile");
        if (!cancelled) {
          setProfile(res.data);
          setProfileStatus("found");
        }
      } catch (err) {
        if (cancelled) return;
        setProfileStatus(err.response?.status === 404 ? "not_found" : "error");
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (profileStatus !== "found") return;
    let cancelled = false;

    const fetchDashboardData = async () => {
      setDataLoading(true);
      const [appRes, cycleRes, disbRes] = await Promise.allSettled([
        API.get("/applications/latest"),
        API.get("/cycles/active"),
        API.get("/applications/disbursement/me"),
      ]);
      if (cancelled) return;

      if (appRes.status === "fulfilled") setApplication(appRes.value.data);
      if (cycleRes.status === "fulfilled") setActiveCycle(cycleRes.value.data);
      if (disbRes.status === "fulfilled") setDisbursement(disbRes.value.data);
      setDataLoading(false);
    };

    fetchDashboardData();
    return () => { cancelled = true; };
  }, [profileStatus]);

  if (profileStatus === "loading") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Loading dashboard...</p>
      </div>
    );
  }

  if (profileStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-4 text-center">
        <AlertCircle size={36} className="text-red-400" />
        <h2 className="text-lg font-semibold text-gray-800">Unable to load your profile</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          There was a problem connecting to the server. Please check your connection and try again.
        </p>
        <button
          onClick={() => setProfileStatus("loading")}
          className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-800 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  const profileComplete = profileStatus === "found";
  const progress = application?.academicProgress || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <main className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-8 py-6 md:py-10 space-y-6">

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="text-2xl font-bold text-gray-800">
            Welcome back, <span className="text-primary">{user?.name?.split(" ")[0] || "Student"}</span>
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">Igembe South Constituency Bursary Portal</p>
        </motion.div>

        {!profileComplete ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-xl p-6 md:p-10 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <UserPlus size={28} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-primary mb-2">Complete Your Profile to Get Started</h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6 leading-relaxed">
              Before you can apply for a bursary, we need a few details about you,
              your personal information, institution details, guardian details, and supporting documents.
              It only takes a few minutes.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-lg mx-auto mb-7 text-left">
              {[
                { icon: User, label: "Personal & school info" },
                { icon: Users2, label: "Guardian details" },
                { icon: ShieldCheck, label: "ID & supporting docs" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-center gap-2.5">
                  <Icon size={16} className="text-primary flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-600">{label}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate("/student-dashboard/profile")}
              className="bg-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-800 transition shadow-md hover:shadow-lg inline-flex items-center gap-2"
            >
              Complete Your Profile <ArrowRight size={16} />
            </button>
          </motion.div>
        ) : (
          <>
            {!dataLoading && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-3"
              >
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <User size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Profile</p>
                    <p className="text-xs font-semibold text-green-600">Complete ✓</p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Application</p>
                    {application ? (
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${STATUS_COLORS[application.status]}`}>
                        {application.status}
                      </span>
                    ) : (
                      <p className="text-xs text-gray-400">Not started</p>
                    )}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                    <Wallet size={18} className="text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Bursary Award</p>
                    <p className="text-xs font-semibold text-gray-700">
                      {disbursement ? formatKES(disbursement.amountAwarded) : "Pending"}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${activeCycle ? "bg-orange-50" : "bg-gray-50"}`}>
                    {activeCycle
                      ? <Clock size={18} className="text-orange-500" />
                      : <CalendarX size={18} className="text-gray-400" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 mb-0.5">Applications</p>
                    <p className={`text-xs font-semibold ${activeCycle ? "text-green-600" : "text-gray-400"}`}>
                      {activeCycle ? "Open" : "Closed"}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {!dataLoading && !activeCycle && !application && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 flex items-start gap-3"
              >
                <CalendarX size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800 text-sm">No Active Application Cycle</p>
                  <p className="text-yellow-700 text-xs mt-0.5">
                    There is no open bursary application cycle at the moment. You will be notified when the next cycle opens.
                  </p>
                </div>
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
            >
              {!application && activeCycle && (
                <div className="text-center py-6 px-4">
                  <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                    <FileText size={28} className="text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-primary mb-2">Apply for Bursary</h2>
                  <p className="text-gray-500 text-sm max-w-md mx-auto mb-2 leading-relaxed">
                    The <strong>{activeCycle.name}</strong> is now open.
                    Deadline:{" "}
                    <strong>
                      {new Date(activeCycle.applicationDeadline).toLocaleDateString("en-KE", { dateStyle: "long" })}
                    </strong>
                  </p>
                  <p className="text-gray-400 text-xs mb-6">
                    Your profile is complete. You can now submit your bursary application.
                  </p>
                  <button
                    onClick={() => navigate("/student-dashboard/application")}
                    className="bg-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-800 transition shadow-md hover:shadow-lg inline-flex items-center gap-2"
                  >
                    Start Application <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {application && (
                <div className="space-y-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                      ${application.status === "APPROVED" ? "bg-green-100"
                        : application.status === "REJECTED" ? "bg-red-100"
                          : "bg-blue-100"}`}
                    >
                      {application.status === "APPROVED"
                        ? <CheckCircle size={22} className="text-green-600" />
                        : application.status === "REJECTED"
                          ? <AlertCircle size={22} className="text-red-500" />
                          : <Clock size={22} className="text-blue-500" />}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800">
                        {application.academicYear} Application
                      </p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[application.status]}`}>
                        {application.status?.replace("_", " ")}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Institution</p>
                      <p className="font-medium text-gray-700 truncate">{profile?.institution?.institutionName || "Not provided"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">
                        {profile?.schoolLevel === "SECONDARY" ? "Grade / Form" : "Year of Study"}
                      </p>
                      <p className="font-medium text-gray-700 flex items-center gap-1">
                        <GraduationCap size={12} className="text-gray-400 flex-shrink-0" />
                        {profile?.schoolLevel === "SECONDARY"
                          ? (progress.formGrade ? getOptionLabel(FORM_GRADE_OPTIONS, progress.formGrade) : "Not provided")
                          : (progress.yearOfStudy || "Not provided")}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Total Fees</p>
                      <p className="font-medium text-gray-700">{formatKES(application.fees?.totalFees)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-0.5">Fee Balance</p>
                      <p className="font-medium text-gray-700">{formatKES(application.fees?.feeBalance)}</p>
                    </div>
                    {disbursement && (
                      <div className="bg-green-50 rounded-xl p-3 col-span-2">
                        <p className="text-xs text-green-600 mb-0.5">Bursary Award</p>
                        <p className="font-bold text-green-800 text-lg">{formatKES(disbursement.amountAwarded)}</p>
                        <p className="text-xs text-green-500 mt-0.5">
                          Status: {disbursement.status} {disbursement.paidAt ? `· Paid ${new Date(disbursement.paidAt).toLocaleDateString("en-KE")}` : ""}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3 pt-1">
                    <button
                      onClick={() => navigate("/student-dashboard/status")}
                      className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition"
                    >
                      View Full Status <ArrowRight size={14} />
                    </button>
                    {application.status === "DRAFT" && activeCycle && (
                      <button
                        onClick={() => navigate("/student-dashboard/application")}
                        className="flex items-center gap-2 border-2 border-primary text-primary px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-50 transition"
                      >
                        Continue Application <ArrowRight size={14} />
                      </button>
                    )}
                    {disbursement?.status === "PAID" && (
                      <button
                        onClick={() => navigate("/student-dashboard/status")}
                        className="flex items-center gap-2 border-2 border-green-500 text-green-700 px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-green-50 transition"
                      >
                        Download Award Letter <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!application && !activeCycle && (
                <div className="text-center py-10 px-4">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-primary mb-2">Profile Complete</h2>
                  <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed">
                    Your profile is ready. You will be notified once the next bursary application cycle opens.
                  </p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
};

export default StudentDashboard;